import React, { useEffect, useMemo, useState } from "react";
import { useLocation } from "wouter";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "../ui/table";
import { useInterfaceRepository, useNonAddedIntExtInterface } from "@/hooks/useLocalStorage";
import { PaginationControl } from "@/widgets/renderPagination";
import { createIntExtInterfaceData } from "@/hooks/useLocalStorage";
import { AlertDialog } from "@/widgets/AlertDailog";

export default function InterfaceSelect() {
    const [location, setLocation] = useLocation();

    const [pageIndex, setPageIndex] = useState(1);
    const [pageSize] = useState(10);
    const [totalCount, setTotalCount] = useState(0);

    const queryParams = new URLSearchParams(window.location.search);
    const formType = queryParams.get("formType");

    const appliedIntType = useMemo(() => {
        console.log("formType", formType)
        return formType === "Internal" ? "0" : "1";
    }, [formType]);

    const { data: interfaceData, isLoading, refetch } = useNonAddedIntExtInterface(
        pageIndex,
        pageSize,
        "",
        "",
        formType?.toLowerCase()
    );

    useEffect(()=>{
        refetch()
    }, [])

    const interfaces = useMemo(() => {
        return (
            interfaceData?.items?.map((item: any) => ({
                id: item.Id,
                name: item.name,
                input: item.input,
                output: item.output,
            })) || []
        );
    }, [interfaceData]);

    useEffect(() => {
        if (interfaceData?.total) {
            setTotalCount(interfaceData.total);
        }
    }, [interfaceData]);

    const [selectedIds, setSelectedIds] = useState<number[]>([]);

    const handleSelect = (id: number, checked: boolean) => {
        if (checked) {
            setSelectedIds((prev) => [...prev, id]);
        } else {
            setSelectedIds((prev) => prev.filter((item) => item !== id));
        }
    };

    const [alertOpen, setAlertOpen] = useState(false);
    const [alertMessage, setAlertMessage] = useState("");
    const [alertStatus, setAlertStatus] = useState(false);

    const { mutateAsync, isPending } = createIntExtInterfaceData();

    const handleSave = async () => {
        try {
            await mutateAsync({
                ids: selectedIds,
            });

            setAlertMessage("Interfaces added successfully!");
            setAlertStatus(true);
            setAlertOpen(true);
            setSelectedIds([]);
        } catch (err) {
            setAlertMessage("Something went wrong!");
            setAlertStatus(false);
            setAlertOpen(true);
        }
    };

    const handleAlertClose = (open: boolean) => {
        setAlertOpen(open);
        if (!open && alertStatus) {
            formType === "Internal" ? setLocation("/") : setLocation("/ExternalInterface");
        }
    };

    if (isLoading) {
        return <div className="p-4">Loading...</div>;
    }

    return (
        <div className="bg-gray-50 min-h-screen p-4">
            <div className="flex justify-between items-center mb-6">
                <h2 className="text-xl font-semibold">
                    Add Internal Interfaces
                </h2>

                <Button variant="outline" onClick={() => setLocation("/")}>
                    Back
                </Button>
            </div>

            <div className="bg-white rounded-lg shadow">
                <Table>
                    <TableHeader>
                        <TableRow>
                            <TableHead>Select</TableHead>
                            <TableHead>ID</TableHead>
                            <TableHead>Interface Name</TableHead>
                            <TableHead>Input</TableHead>
                            <TableHead>Output</TableHead>
                        </TableRow>
                    </TableHeader>

                    <TableBody>
                        {interfaces.map((item) => (
                            <TableRow key={item.id}>
                                <TableCell>
                                    <Checkbox
                                        checked={selectedIds.includes(item.id)}
                                        onCheckedChange={(val: boolean) =>
                                            handleSelect(item.id, val)
                                        }
                                    />
                                </TableCell>
                                <TableCell>{item.id}</TableCell>
                                <TableCell>{item.name}</TableCell>
                                <TableCell>{item.input}</TableCell>
                                <TableCell>{item.output}</TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>

                <div className="mt-2 p-2">
                    <PaginationControl
                        pageIndex={pageIndex}
                        pageSize={pageSize}
                        totalCount={totalCount}
                        onPageChange={setPageIndex}
                    />
                </div>
            </div>

            <div className="mt-6 flex justify-center">
                <Button
                    disabled={selectedIds.length === 0 || isPending}
                    onClick={handleSave}
                    className="bg-workfinity-500 hover:bg-workfinity-600"
                >
                    {isPending ? "Adding..." : "Add Interface"}
                </Button>
            </div>

            <AlertDialog
                open={alertOpen}
                onOpenChange={handleAlertClose}
                message={alertMessage}
                status={alertStatus}
            />
        </div>
    );
}